<script lang="ts">
    import { PUBLIC_ENVIRONMENT } from "$env/static/public";
    import { afterNavigate, invalidate, invalidateAll } from "$app/navigation";

    import Analytics from "$lib/components/Analytics.svelte";
    import BookACall from "$lib/modals/BookACall.svelte";
    import Error from "$lib/modals/Error.svelte";
    import ManageDraftUsers from "$lib/modals/ManageDraftUsers.svelte";
    import SignIn from "$lib/modals/SignIn.svelte";
    import UpdateAccount from "$lib/modals/UpdateAccount.svelte";
    import UpdatePassword from "$lib/modals/UpdatePassword.svelte";
    import UploadAvatar from "$lib/modals/UploadAvatar.svelte";
    import SubscribeScoutCapture from "$lib/modals/SubscribeScoutCapture.svelte";
    import SubscribeScoutWriter from "$lib/modals/SubscribeScoutWriter.svelte";
    import EditApplicationName from "$lib/modals/EditApplicationName.svelte";
    import CreateNewApplication from "$lib/modals/CreateNewApplication.svelte";
    import AddCategory from "$lib/modals/AddCategory.svelte";
    import EditSection from "$lib/modals/EditSection.svelte";
    import EditCollaborators from "$lib/modals/EditCollaborators.svelte";
    import EditTags from "$lib/modals/EditTags.svelte";
    import ConfirmGenerate from "$lib/modals/ConfirmGenerate.svelte";
    import AddQARow from "$lib/modals/AddQARow.svelte";
    import ConfirmQADelete from "$lib/modals/ConfirmQADelete.svelte";
    import InsertQAPair from "$lib/modals/InsertQAPair.svelte";

    import { arrow, autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";

    import * as Sentry from "@sentry/sveltekit";

    import {
        getModalStore,
        initializeStores,
        Modal,
        Toast,
        type ModalComponent,
        storeHighlightJs,
        storePopup,
    } from "@skeletonlabs/skeleton";

    import { type AfterNavigate } from "@sveltejs/kit";

    import hljs from "highlight.js/lib/core";
    import css from "highlight.js/lib/languages/css";
    import javascript from "highlight.js/lib/languages/javascript";
    import typescript from "highlight.js/lib/languages/typescript";
    import xml from "highlight.js/lib/languages/xml"; // for HTML
    import "highlight.js/styles/github-dark.css";

    import { onMount } from "svelte";

    import "../app.postcss";

    hljs.registerLanguage("xml", xml); // for HTML
    hljs.registerLanguage("css", css);
    hljs.registerLanguage("javascript", javascript);
    hljs.registerLanguage("typescript", typescript);
    storeHighlightJs.set(hljs);

    storePopup.set({ computePosition, autoUpdate, flip, shift, offset, arrow });

    export let data;
    let { session, supabase, user } = data;
    $: ({ session, supabase, user } = data);

    let innerWidth = 1440;
    $: mobile = innerWidth < 640;

    initializeStores();

    const modalStore = getModalStore();

    const modalRegistry: Record<string, ModalComponent> = {
        signIn: { ref: SignIn },
        updatePassword: { ref: UpdatePassword },
        bookACall: { ref: BookACall },
        error: { ref: Error },
        updateAccount: { ref: UpdateAccount },
        subscribeScoutCapture: { ref: SubscribeScoutCapture },
        subscribeScoutWriter: { ref: SubscribeScoutWriter },
        manageDraftUsers: { ref: ManageDraftUsers },
        uploadAvatar: { ref: UploadAvatar },
        editApplicationName: { ref: EditApplicationName },
        createNewApplication: { ref: CreateNewApplication },
        addCategory: { ref: AddCategory },
        editSection: { ref: EditSection },
        editCollaborators: { ref: EditCollaborators },
        editTags: { ref: EditTags },
        confirmGenerate: { ref: ConfirmGenerate },
        addQARow: { ref: AddQARow },
        confirmQADelete: { ref: ConfirmQADelete },
        insertQAPair: { ref: InsertQAPair },
    };

    afterNavigate((params: AfterNavigate) => {
        const isNewPage = params.from?.url.pathname !== params.to?.url.pathname;
        const elemPage = document.querySelector("#page");
        if (isNewPage && elemPage !== null) {
            elemPage.scrollTop = 0;
        }
    });

    supabase.auth.onAuthStateChange((event, session) => {
        if (event === "SIGNED_IN") {
            invalidateAll().then(() => {
                modalStore.close();
            });
        }
    });

    onMount(() => {
        if (user) {
            Sentry.setUser({ id: user.id, email: user.email, name: user.full_name });
        }

        const { data } = supabase.auth.onAuthStateChange((event, _session) => {
            if (_session?.expires_at !== session?.expires_at) {
                invalidate("supabase:auth");
            }
        });

        return () => data.subscription.unsubscribe();
    });
</script>

<svelte:head>
    {#if PUBLIC_ENVIRONMENT === "staging"}
        <meta name="robots" content="noindex" />
    {/if}
</svelte:head>

<svelte:window bind:innerWidth />

<Analytics />
<Modal components={modalRegistry} />
<Toast />

<slot />
